import React, { useEffect } from 'react';
import { Box, styled, Typography } from '@mui/material';
import EqualisationsConcertina from 'components/EqualisationElements/EqualisationsConcertina';
import InteractiveTable, {
  Column,
  useRowsEdit,
} from 'components/InteractiveTable';
import { AppSimpleSelect } from 'components/FormElements';
import AppNumberInput from 'components/FormElements/AppNumberInput';
import { FeesEqualisationTreatmentLabels } from 'further-ui/labels';
import { FeesEqualisationTreatment } from 'further-types/lp/equalisation';
import { numberToCurrencyString } from 'further-ui/utils';
import { EqualisationFeesFormValues } from './FormWrapper';
import useEqualiseFees from '../../hooks/useEqualiseFees';
import { sumBy } from 'lodash';

const StyledTable = styled(InteractiveTable)({
  '& .MuiDataGrid-cell--editing': {
    '&[data-field="equalisationSalesTaxAmountLcy"], &[data-field="equalisationFeeExTaxLcy"]':
      {
        '&:focus-within': {
          outline: 'none',
        },
      },
  },
});

interface FeeCardRowProps {
  index: number;
  isLastElement: boolean;
  lpFeeTemplates: Array<{
    id: string;
    name: string;
  }>;
  value: EqualisationFeesFormValues['totalCommitmentFees'][number];
  baseCurrency: string;
  onChange: (
    value: EqualisationFeesFormValues['totalCommitmentFees'][number],
  ) => void;
}

const FeeCardRow: React.FC<FeeCardRowProps> = ({
  index,
  isLastElement,
  lpFeeTemplates,
  value,
  baseCurrency,
  onChange,
}) => {
  const { rowsEditModel, editManyRows, resetRowsEditModel } = useRowsEdit();
  const {
    apiRef,
    updateSingleFeeField,
    recalculateFromTreatmentChange,
    recalculateFromPercentageChange,
    updateGridRows,
  } = useEqualiseFees({
    index,
    baseCurrency,
    editManyRows,
    resetRowsEditModel,
  });

  useEffect(() => {
    if (!value?.lpFees || value.lpFees.length === 0) return;
    editManyRows(
      value.lpFees.map((row, idx) => ({ id: row.id || `${index}-${idx}` })),
    );
  }, []);

  const columns: Array<Column> = [
    {
      field: 'accrualDate',
      headerName: 'Accrual date',
      renderCell: ({ row }) => row.accrualDate?.toLocalDateLabel() ?? '-',
      flex: 1,
      sortable: false,
    },
    {
      field: 'feeTemplateName',
      headerName: 'Fee template name',
      flex: 1,
      sortable: false,
    },
    {
      field: 'totalCommitmentLcy',
      headerName: 'Total commitment',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.totalCommitmentLcy, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      flex: 1,
      sortable: false,
    },
    {
      field: 'equalisationFeeExTaxLcy',
      headerName: 'Fee (ex tax)',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.equalisationFeeExTaxLcy, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      flex: 1,
      editable: true,
      renderEditCell: ({ row, api, field, id }) => (
        <AppNumberInput
          value={row.equalisationFeeExTaxLcy}
          onChange={(newValue) => {
            api.setEditCellValue({
              id,
              field,
              value: newValue,
            });
            updateSingleFeeField(
              String(id),
              'equalisationFeeExTaxLcy',
              newValue,
              row.rowIndex,
            );
          }}
          currencyStartAdornment
          decimals={2}
          min={0}
          sx={{ marginTop: '2px' }}
        />
      ),
    },
    {
      field: 'equalisationSalesTaxAmountLcy',
      headerName: 'Sales tax',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.equalisationSalesTaxAmountLcy, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      flex: 1,
      editable: true,
      renderEditCell: ({ row, api, field, id }) => (
        <AppNumberInput
          value={row.equalisationSalesTaxAmountLcy}
          onChange={(newValue) => {
            api.setEditCellValue({
              id,
              field,
              value: newValue,
            });
            updateSingleFeeField(
              String(id),
              'equalisationSalesTaxAmountLcy',
              newValue,
              row.rowIndex,
            );
          }}
          currencyStartAdornment
          decimals={2}
          min={0}
          sx={{ marginTop: '2px' }}
        />
      ),
    },
    {
      field: 'equalisationFeeIncTaxLcy',
      headerName: 'Fee (inc tax)',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.equalisationFeeIncTaxLcy, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      flex: 1,
    },
  ];

  return (
    <EqualisationsConcertina
      key={index}
      isExpandedByDefault={value?.lpFees?.length > 0}
      gridTemplateColumns="20% 16% 16% 20% 12% 14% 2%"
      isLastElement={isLastElement}
      expandedContent={
        <StyledTable
          id={`equalise-fees-${index}`}
          apiRef={apiRef}
          columns={columns}
          rows={(value?.lpFees ?? []).map((fee, feeIndex) => ({
            ...fee,
            id: fee.id || `${index}-${feeIndex}`,
            baseCurrency,
            rowIndex: feeIndex,
          }))}
          rowsEditModel={rowsEditModel}
          scrollable
        />
      }
      concertinaItems={[
        <Typography variant="body1" key="investor-name">
          {value?.investorName ?? ''}
        </Typography>,
        <Typography variant="body1" key="status">
          {value?.investorStatus ?? ''}
        </Typography>,
        <Box
          sx={{ paddingRight: 6 }}
          key="fee-template-select"
          onClick={(e) => e.stopPropagation()}
        >
          <AppSimpleSelect
            fullWidth
            options={[
              { value: 'none', label: 'None' },
              ...lpFeeTemplates.map((template) => ({
                value: template.id,
                label: template.name,
              })),
            ]}
            value={value?.lpFeeTemplateId ?? ''}
            onChange={(event) => {
              const newValue = event.target.value as string;
              if (newValue === 'none') {
                resetRowsEditModel();
                onChange({
                  ...value,
                  lpFeeTemplateId: 'none',
                  lpFees: [],
                  totalEqualisationFeeIncTaxLcy: 0,
                  feesEqualisationTreatment: FeesEqualisationTreatment.None,
                  feesEqualisationTreatmentPercentage: 0,
                });
                setTimeout(() => {
                  updateGridRows({
                    ...value,
                    lpFeeTemplateId: 'none',
                    lpFees: [],
                    totalEqualisationFeeIncTaxLcy: 0,
                    feesEqualisationTreatment: FeesEqualisationTreatment.None,
                    feesEqualisationTreatmentPercentage: 0,
                  });
                }, 25);
                return;
              }

              const newLpFees = value.lpFeesByTemplate?.[newValue].lpFees || [];
              const newTotal = sumBy(
                newLpFees,
                (fee: { equalisationFeeIncTaxLcy: number }) =>
                  fee.equalisationFeeIncTaxLcy || 0,
              );

              const updatedValue = {
                ...value,
                lpFeeTemplateId: newValue,
                lpFees: newLpFees,
                totalEqualisationFeeIncTaxLcy: newTotal,
                feesEqualisationTreatment:
                  value.lpFeesByTemplate?.[newValue]?.feesEqualisationTreatment,
                feesEqualisationTreatmentPercentage:
                  value.lpFeesByTemplate?.[newValue]
                    ?.feesEqualisationTreatmentPercentage,
              };

              resetRowsEditModel();

              onChange(updatedValue);

              setTimeout(() => {
                updateGridRows(updatedValue);
                setTimeout(() => {
                  editManyRows(newLpFees.map((fee) => ({ id: fee.id })));
                }, 30);
              }, 25);
            }}
          />
        </Box>,
        <Box
          sx={{ paddingRight: 6 }}
          key="treatment-select"
          onClick={(e) => e.stopPropagation()}
        >
          <AppSimpleSelect
            fullWidth
            options={Object.entries(FeesEqualisationTreatmentLabels).map(
              ([optionValue, label]) => ({
                value: optionValue,
                label,
              }),
            )}
            value={value?.feesEqualisationTreatment}
            onChange={(event) => {
              const treatment = event.target.value as FeesEqualisationTreatment;
              recalculateFromTreatmentChange(
                value,
                treatment,
                onChange,
                value?.lpFeeTemplateId ?? 'none',
              );
            }}
          />
        </Box>,
        <Box
          key="percentage-input"
          onClick={(e) => e.stopPropagation()}
          sx={{ paddingRight: 6 }}
        >
          {value?.feesEqualisationTreatment ===
            FeesEqualisationTreatment.Partial && (
            <AppNumberInput
              onChange={(percentage) => {
                recalculateFromPercentageChange(value, percentage, onChange);
              }}
              min={0}
              max={100}
              decimals={2}
              placeholder={'Partial catch-up percentage'}
              value={value?.feesEqualisationTreatmentPercentage ?? 0}
              percentEndAdornment
            />
          )}
        </Box>,
        <Typography variant="body1" key="fees-total">
          {numberToCurrencyString(value?.totalEqualisationFeeIncTaxLcy, {
            fallback: '-',
            currency: baseCurrency,
          })}
        </Typography>,
      ]}
    />
  );
};

export default FeeCardRow;
